import { createTheme, ThemeOptions } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';

const getDesignTokens = (mode: PaletteMode): ThemeOptions => ({
  palette: {
    mode,
    primary: {
      main: '#20C997', // Main Teal
      light: '#4EDBB3',
      dark: '#179E76',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#4C9EEB', // Accent Blue
      light: '#70B1EF',
      dark: '#356EA4',
      contrastText: '#ffffff',
    },
    background: {
      default: mode === 'light' ? '#F8F9FA' : '#121212',
      paper: mode === 'light' ? '#ffffff' : '#1E1E1E',
    },
    text: {
      primary: mode === 'light' ? '#000000' : '#ffffff',
      secondary: mode === 'light' ? '#6C757D' : '#9E9E9E',
    },
    divider: mode === 'light' ? '#E9ECEF' : '#3A3A3A',
    // Custom category colors
    biology: { main: '#7ED957' },
    physics: { main: '#FFD93D' },
    chemistry: { main: '#4C9EEB' },
    economics: { main: '#9B59B6' },
  } as any, // Cast to any to allow custom properties in palette
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 700 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 500 },
    body1: { fontSize: '1rem', lineHeight: 1.5 },
    body2: { fontSize: '0.875rem', lineHeight: 1.57 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          backgroundColor: mode === 'light' ? '#ffffff' : '#2C2C2C', // darkCard for dark mode
          boxShadow: mode === 'light'
            ? '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)'
            : '0 1px 3px 0 rgb(0 0 0 / 0.3), 0 1px 2px -1px rgb(0 0 0 / 0.3)',
          backgroundImage: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: mode === 'light' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(18, 18, 18, 0.8)', // darkBackground
          backdropFilter: 'blur(8px)',
          color: mode === 'light' ? '#000000' : '#ffffff',
          boxShadow: 'none',
          borderBottom: `1px solid ${mode === 'light' ? '#E9ECEF' : '#3A3A3A'}`, // divider
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: mode === 'light' ? '#ffffff' : '#1E1E1E', // darkSurface
          borderRight: `1px solid ${mode === 'light' ? '#E9ECEF' : '#3A3A3A'}`, // divider
        },
      },
    },
  },
});

export const theme = (mode: PaletteMode) => createTheme(getDesignTokens(mode));
